// Quick Add for one phase, opened from the Project Viewer's Time Entries tab.
// Same dialog as My Time Entry, locked to the phase and showing only the
// suggestions matched to it. Each entry posts straight to BQE, adding onto
// what BQE already has for that activity and day; My Time Entry drafts play
// no part here.

// DEPENDENCIES
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import {
	Alert,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	Snackbar,
	useMediaQuery,
} from "@mui/material";

// COMPONENTS
import QuickAddDialog from "./QuickAddDialog";

// API IMPORTS
import { getAPICall, postAPICall } from "../../config/apiCalls";

// DATA / HELPERS
import { TimeEntryDataContext, useTimeEntryDataStore } from "./timeEntryData";
import { useSuggestions } from "./timeEntrySuggestions";
import {
	addDays,
	appendMemo,
	buildWeek,
	fmtHours,
	isoOf,
	mondayOf,
	startOfDay,
	toHours,
	weeklyTargetFor,
} from "./timeEntryUtils";

// STYLES
import "./TimeEntry.css";

const round1 = (n) => Math.round(n * 10) / 10;

// FUNCTIONAL COMPONENT
export default function ProjectQuickAdd({ open, onClose, projectId, onSent }) {
	// MSAL
	const { instance, accounts } = useMsal();

	// Same breakpoint as My Time Entry
	const isMobile = useMediaQuery("(max-width: 767px)");

	// DATA (employee, projects, activities)
	const data = useTimeEntryDataStore(instance, accounts[0]);
	const {
		employee,
		projectsState,
		loadBasics,
		refreshBasicsIfStale,
		projects,
		registerEntries,
	} = data;

	const today = useMemo(() => startOfDay(new Date()), []);

	// STATES
	const [quickDate, setQuickDate] = useState(today);
	// Per week (Monday ISO): { status: loading | ready | error, entries }
	const [weeks, setWeeks] = useState({});
	const [snack, setSnack] = useState(null); // { severity, text }

	// REFS
	const basicsRequested = useRef(false);

	const weekKey = isoOf(mondayOf(quickDate));
	const week = useMemo(() => buildWeek(weekKey, today), [weekKey, today]);
	const suggestions = useSuggestions(
		instance,
		accounts[0],
		weekKey,
		open && !isMobile,
	);

	// The employee's BQE entries for a week; a reload keeps the old ones on
	// screen until the new ones arrive
	const loadWeek = useCallback(
		async (key) => {
			setWeeks((w) => ({
				...w,
				[key]: {
					entries: [],
					...w[key],
					status: w[key]?.status === "ready" ? "ready" : "loading",
				},
			}));
			try {
				const res = await getAPICall(
					instance,
					accounts[0],
					"/api/time-entry/week",
					{ start: key },
				);
				const entries = res.data.entries || [];
				registerEntries(entries);
				setWeeks((w) => ({
					...w,
					[key]: { status: "ready", entries },
				}));
			} catch (error) {
				console.error("ProjectQuickAdd - loadWeek - error:", error);
				setWeeks((w) => ({
					...w,
					[key]: { entries: [], ...w[key], status: "error" },
				}));
			}
		},
		[instance, accounts, registerEntries],
	);

	// USE EFFECTS
	useEffect(() => {
		if (!open) return;
		if (!basicsRequested.current) {
			basicsRequested.current = true;
			loadBasics();
		} else {
			refreshBasicsIfStale();
		}
	}, [open]); // eslint-disable-line react-hooks/exhaustive-deps

	// Re-read the week each time the dialog opens or moves to another week, so
	// what's added on top always starts from what BQE has now
	useEffect(() => {
		if (open) loadWeek(weekKey);
	}, [open, weekKey]); // eslint-disable-line react-hooks/exhaustive-deps

	// HELPERS
	const entriesOn = (date) =>
		(weeks[isoOf(mondayOf(date))]?.entries || []).filter(
			(e) => e.date === isoOf(date),
		);

	const hoursOnDate = (date) =>
		entriesOn(date).reduce((a, e) => a + e.hours, 0);

	const projectHoursOnDate = (pid, date) =>
		entriesOn(date)
			.filter((e) => e.projectId === pid)
			.reduce((a, e) => a + e.hours, 0);

	const cellEntries = (pid, activityId, date) =>
		entriesOn(date).filter(
			(e) => e.projectId === pid && e.activityId === activityId,
		);

	// What BQE has for an activity on a day, shaped like a grid cell (null: nothing)
	const existingCell = (pid, activityId, date) => {
		const group = cellEntries(pid, activityId, date);
		if (!group.length) return null;
		const first = group[0];
		return {
			hours: String(round1(group.reduce((a, e) => a + e.hours, 0))),
			memo: group
				.map((e) => e.memo)
				.filter(Boolean)
				.join("; "),
			billable: first.billable,
			overtime: first.overtime,
			extraTime: first.extraTime,
			compTime: first.compTime,
			lockReason: group.map((e) => e.lockReason).find(Boolean) || null,
		};
	};

	// Posts one entry to BQE. Returns the confirmation to show, or false (and
	// explains) when it wasn't saved.
	const sendEntry = async ({
		projectId: pid,
		activityId,
		hours,
		memo,
		date,
		billable,
		compTime,
	}) => {
		const key = isoOf(mondayOf(date));
		if (weeks[key]?.status !== "ready") {
			setSnack({
				severity: "info",
				text: "That week is still loading from BQE. Try again in a moment.",
			});
			return false;
		}
		const group = cellEntries(pid, activityId, date);
		if (group.some((e) => e.lockReason)) {
			setSnack({
				severity: "warning",
				text: "That entry is already submitted, approved or billed in BQE, so hours can't be added to it here.",
			});
			return false;
		}
		const cell = {
			projectId: pid,
			activityId,
			date: isoOf(date),
			hours: round1(
				group.reduce((a, e) => a + e.hours, 0) + toHours(hours),
			),
			memo: appendMemo(
				group
					.map((e) => e.memo)
					.filter(Boolean)
					.join("; "),
				memo,
			),
			billable,
			compTime,
			baseEntries: group.map((e) => ({ id: e.id, version: e.version })),
		};
		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/time-entry/send",
				{
					start: key,
					end: isoOf(addDays(mondayOf(date), 6)),
					dryRun: false,
					cells: [cell],
				},
			);
			const result = res.data.results[0];
			loadWeek(key);
			if (result.status === "created" || result.status === "updated") {
				if (onSent) onSent();
				return fmtHours(toHours(hours)) + " hrs sent to BQE";
			}
			if (result.status === "pending") {
				if (onSent) onSent();
				setSnack({ severity: "info", text: result.message });
				return fmtHours(toHours(hours)) + " hrs sent to BQE";
			}
			setSnack({
				severity: result.status === "error" ? "error" : "warning",
				text:
					"Not sent: " +
					(result.message || "BQE didn't take this entry."),
			});
			return false;
		} catch (error) {
			console.error("ProjectQuickAdd - sendEntry - error:", error);
			// A 400 is refused before anything is written (e.g. over 24 hrs on a day)
			setSnack({
				severity: "error",
				text:
					error.response && error.response.status === 400
						? (error.response.data.error ||
								"Something isn't valid") + ". Nothing was sent."
						: "Something went wrong while sending. Check the week in My Time Entry to see what reached BQE.",
			});
			loadWeek(key);
			return false;
		}
	};

	// A phase that isn't active or on hold can't take time
	const unknownProject =
		projectsState === "ready" && !projects.some((p) => p.id === projectId);

	const debugNotice = employee && employee.debugIdentity && (
		<Alert severity="error" className="teQuick-alert">
			Debug identity: you're using this as{" "}
			<strong>{employee.name}</strong>. Sending writes their real time.
		</Alert>
	);

	// RENDER
	return (
		<TimeEntryDataContext.Provider value={data}>
			{projectsState === "ready" && !unknownProject ? (
				<QuickAddDialog
					mobile={isMobile}
					open={open}
					onClose={onClose}
					week={week}
					quickDate={quickDate}
					onPickDate={(d) => setQuickDate(startOfDay(d))}
					today={today}
					hoursOnDate={hoursOnDate}
					projectHoursOnDate={projectHoursOnDate}
					dailyTarget={
						weeklyTargetFor({
							hours_per_week: employee && employee.hoursPerWeek,
						}) / 5
					}
					existingCell={existingCell}
					suggestions={suggestions}
					onSave={sendEntry}
					lockedProjectId={projectId}
					addLabel="Send to BQE"
					notice={debugNotice}
				/>
			) : (
				<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
					<DialogTitle className="teDialogTitle">
						Quick Add
					</DialogTitle>
					<DialogContent className="teDialogContent">
						<p className="teDialogText">
							{projectsState === "loading"
								? "Loading…"
								: projectsState === "auth"
									? "Your Microsoft sign-in has expired. Reload the page to sign in again."
									: projectsState === "error"
										? "Couldn't load your projects from BQE."
										: "Time can only be entered on active or on-hold phases."}
						</p>
						<div className="dialogButtons">
							{projectsState === "error" && (
								<Button
									variant="outlined"
									onClick={() => loadBasics()}
								>
									Try again
								</Button>
							)}
							<Button
								variant="contained"
								disableElevation
								onClick={onClose}
							>
								Close
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			)}

			<Snackbar
				open={Boolean(snack)}
				autoHideDuration={6000}
				onClose={() => setSnack(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				{snack ? (
					<Alert
						severity={snack.severity}
						variant="filled"
						className="teSnack"
						onClose={() => setSnack(null)}
					>
						{snack.text}
					</Alert>
				) : (
					<span />
				)}
			</Snackbar>
		</TimeEntryDataContext.Provider>
	);
}
